import type { ReactNode } from "react";
import { AlertTriangle, RotateCw } from "lucide-react";
import type { ApiError } from "../api/client";
import { w } from "../i18n";
import { STATUS_GLYPH, STATUS_TONE, type UserStatus } from "../status";

export function StatusBadge({ status, label, subtle = false }: { status: UserStatus; label?: string; subtle?: boolean }) {
  const text = label ?? w(`status.${status}`);
  return <span className={`ws-status ws-tone-${STATUS_TONE[status]} ${subtle ? "subtle" : ""}`}>
    <span className={`ws-status-glyph ${status === "processing" ? "ws-spin" : ""}`} aria-hidden="true">{STATUS_GLYPH[status]}</span>
    {text}
  </span>;
}

export function Skeleton({ width = "100%", height = 12 }: { width?: number | string; height?: number }) {
  return <span className="ws-skeleton" style={{ width, height }} aria-hidden="true" />;
}

/** Placeholder rows shaped like the table they stand for. */
export function SkeletonRows({ columns, rows = 5 }: { columns: (number | string)[]; rows?: number }) {
  return <>{Array.from({ length: rows }, (_, row) => <tr key={row} aria-hidden="true">
    {columns.map((width, column) => <td key={column}><Skeleton width={width} /></td>)}
  </tr>)}</>;
}

export function SkeletonBlock({ lines = 4 }: { lines?: number }) {
  return <div className="ws-stack" style={{ gap: 10 }} role="status" aria-label={w("common.loading")}>
    {Array.from({ length: lines }, (_, index) => <Skeleton key={index} width={`${90 - index * 12}%`} />)}
  </div>;
}

export function EmptyState({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return <div className="ws-empty"><h3>{title}</h3>{body && <p>{body}</p>}{action}</div>;
}

/** What failed, whether the user can recover, and the request reference for support. */
export function ErrorState({ title, error, onRetry, note }: {
  title: string; error?: ApiError | null; onRetry?: () => void; note?: string;
}) {
  const message = error?.code === "NETWORK_ERROR" ? w("common.networkError") : error?.message || w("common.genericError");
  return <div className="ws-alert ws-tone-negative" role="alert">
    <AlertTriangle size={18} aria-hidden="true" style={{ color: "var(--tone)", flex: "none", marginTop: 2 }} />
    <div>
      <h3>{title}</h3>
      <p>{message}</p>
      {note && <p className="ws-small ws-muted">{note}</p>}
      {error?.requestId && <p className="ws-small ws-faint">{w("common.requestId", { id: error.requestId })}</p>}
      {onRetry && <div className="ws-alert-actions"><button className="ws-btn sm" onClick={onRetry}><RotateCw size={14} />{w("common.retry")}</button></div>}
    </div>
  </div>;
}

export function Notice({ tone = "info", title, children, actions }: {
  tone?: "info" | "caution" | "neutral" | "positive"; title?: string; children: ReactNode; actions?: ReactNode;
}) {
  return <div className={`ws-alert ws-tone-${tone}`}>
    <div>{title && <h3>{title}</h3>}<div>{children}</div>{actions && <div className="ws-alert-actions">{actions}</div>}</div>
  </div>;
}

export function PageHeader({ eyebrow, title, lead, actions }: {
  eyebrow?: ReactNode; title: ReactNode; lead?: ReactNode; actions?: ReactNode;
}) {
  return <header className="ws-page-head">
    <div>{eyebrow && <div className="ws-eyebrow">{eyebrow}</div>}<h1 className="ws-page-title">{title}</h1>
      {lead && <p className="ws-page-lead">{lead}</p>}</div>
    {actions && <div className="ws-row">{actions}</div>}
  </header>;
}
